import { formatDate, formatDateToYMD } from './dateFormat';
import { parseApproximateComponents, parseTimeInterval } from './taskLogic';
import { hasNoDate, timeIntervalParts } from './taskStatus';

export { timeIntervalParts };

// A task's dates (Documentation/plans/planning-dates.md in agromate-api):
// - Time interval (timeinterval): the customer's expected date - set when the task is created, locked after
// - Planned (planned_interval as chosen, planned_date its date): Agrolab's planning, in any format
// - Done date (donedate): when it was done

/** Whether the task has a Planned value. */
export function hasPlanned(task) {
  return Boolean(task?.planned_interval || task?.planned_date);
}

/** The Planned value as written ("medio May 2027", "22-10-2026", "after harvest"); '' without one. */
export function plannedText(task) {
  return formatDate(task?.planned_interval || task?.planned_date || '');
}

/** A value that is a plain date ("2027-05-03"). */
export const isIsoDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || '').trim());

/**
 * Which way a timing value is entered: the date picker for an empty or plain date, the week numbers for
 * "week <N> <Year>", the month position for "primo/medio/ultimo <Month> <Year>" (Seasons give the same
 * shape), free text otherwise (e.g. "after harvest").
 */
export function timingMode(value) {
  if (!value || isIsoDate(value)) return 'date';
  const approx = parseApproximateComponents(value);
  if (approx?.mode === 'week') return 'week';
  if (approx?.mode === 'month') return 'approx';
  return 'text';
}

/** The date a Planned value gives ("YYYY-MM-DD"): itself, or that of a month, week or season; null for words. */
export function plannedDateOf(value) {
  if (!value) return null;
  if (isIsoDate(value)) return String(value).trim();
  if (timingMode(value) === 'text') return null;
  const date = parseTimeInterval(value);
  return date instanceof Date && !Number.isNaN(date.getTime()) ? formatDateToYMD(date) : null;
}

/** The customer's Time interval as shown: "<date> · <text>" (e.g. "15-04-2027 · BBCH 31-32"), either alone, or ''. */
export function expectedText(task) {
  const { date, text } = timeIntervalParts(task);
  return [formatDate(date), formatDate(text)].filter(Boolean).join(' · ');
}

export const NO_DATE_TEXT = 'No date';

/**
 * A task card's date line (Time interval or Planned date): "No date" for none, "?" or "TBD" (as the No date filter),
 * else as written - a date as DD-MM-YYYY, words such as "Spring 2025" or "medio May 2027" as they are.
 */
export function dateLineText(value) {
  return hasNoDate(value) ? NO_DATE_TEXT : formatDate(String(value).trim());
}

/** The Time interval and the Planned date of a task, for its two date lines. */
export function taskDateValues(task) {
  return {
    timeInterval: expectedText(task),
    planned: task?.planned_interval || task?.planned_date || '',
  };
}
